import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import SEO from '../SEO'
import { generateTreatySchema } from '../../services/schemaGenerators'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PrintRoundedIcon from '@mui/icons-material/PrintRounded'
import MailRoundedIcon from '@mui/icons-material/MailRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SortByAlphaRoundedIcon from '@mui/icons-material/SortByAlphaRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import { ShareRounded } from '@mui/icons-material'
import { Enhanced404Handler } from '../Error404'
import { Link } from '@mui/material'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import _ from 'lodash'

import {
  handleEmail,
  handleOpenInNewTab,
} from '../../services/articleCardHandlers'

import { parseHtmlSanitizeAddTargetToLinks } from '../../services/utils'
import { fetchTaxTreatyBySlug } from '../../services/articleAPISevicesUsingFetch'
import apiService from '../../services/ApiService'
import FeedbackForm from '../FeedbackForm'
import UnofficialTranslationNotice from '../UnofficialTranslationNotice'
import BetaVersionNotice from '../BetaVersionNotice'
// Module-level cache
const contentCache = {}

const TaxTreatyArticle = ({
  treaty,
  searchQuery,
  showSnackbar,
  isInModal = false, // Prop to indicate if component is inside a modal
  parentContainerRef = null, // Reference to parent container for scrolling in modal mode
}) => {
  const [treatyContent, setTreatyContent] = useState(null)
  const [loading, setLoading] = useState(true) // The loading state if initially set to false, then set to true when fetching begins. Be aware it might briefly show "No content available" before showing the loading skeleton.
  const [error, setError] = useState(null)
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const componentRef = useRef()
  const fetchedRef = useRef(false) // Track fetch state to prevent duplicate calls

  // we need to sort lexicographically, all the definitions of Article 1 of CIT law
  const [parsedContent, setParsedContent] = useState(null)
  const [sortedContent, setSortedContent] = useState(null)
  const [contentHtml, setContentHtml] = useState(null)
  const [gtlNotesContent, setGtlNotesContent] = useState(null)
  const [gtlNotesTitle, setGtlNotesTitle] = useState('GTL Notes')
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)
  // const [isUnofficialTranslation, setIsUnofficialTranslation] = useState(false)

  const themeColors = {
    primary: '#232536',
    secondary: '#ffcf51',
    hover: '#3a3e5a',
  }
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const buttonBorderRadius = '28px'

  // const baseUrl = process.env.REACT_APP_BACKEND_URL

  // Decide which container to use for scroll detection and scrollToTop action
  const scrollContainer = isInModal ? parentContainerRef : window

  // Show back to top button when scrolled down
  useEffect(() => {
    if (!scrollContainer) return

    const handleScroll = () => {
      // For window, use scrollY; for element, use scrollTop
      const scrollPosition = isInModal
        ? scrollContainer.current?.scrollTop
        : window.scrollY

      if (scrollPosition > 300) {
        setShowBackToTop(true)
      } else {
        setShowBackToTop(false)
      }
    }

    // Add scroll listener to the appropriate container
    if (isInModal && scrollContainer.current) {
      scrollContainer.current.addEventListener('scroll', handleScroll)
    } else {
      window.addEventListener('scroll', handleScroll)
    }

    return () => {
      // Clean up scroll listener
      if (isInModal && scrollContainer.current) {
        scrollContainer.current.removeEventListener('scroll', handleScroll)
      } else {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [isInModal, scrollContainer])

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (isInModal && parentContainerRef?.current) {
      parentContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }, [isInModal, parentContainerRef])

  const extractGtlNotes = useCallback((htmlContent) => {
    try {
      if (!htmlContent) return null
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, 'text/html')
      // Debug: log all h3.linktogtl elements
      const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
      // Look for the GTL Notes heading (not the "See GTL Notes" one)
      let gtlHeading = null
      for (const heading of allGtlHeadings) {
        const anchorTag = heading.querySelector('a')
        if (anchorTag) {
          const text = anchorTag.textContent.trim()
          const id = anchorTag.id
          // Look for the actual GTL Notes heading (usually has id="bookmark" and text starts with "GTL Notes")
          if (text.startsWith('GTL Notes') && id === 'bookmark') {
            gtlHeading = heading
            break
          }

          // Alternative: look for heading that doesn't contain brackets (not "[See GTL Notes]")
          else if (
            text.includes('GTL Notes') &&
            !text.includes('[') &&
            !text.includes(']')
          ) {
            gtlHeading = heading

            break
          }
        }
      }
      if (!gtlHeading) {
        // console.log('No GTL Notes heading found')
        return null
      }
      // Get the title from the anchor tag
      const anchorTag = gtlHeading.querySelector('a')
      const title = anchorTag ? anchorTag.textContent.trim() : 'GTL Notes'
      // Find all content after the GTL Notes heading
      let currentElement = gtlHeading.nextElementSibling
      const gtlContent = []
      // Get all following sibling elements until we hit another h3 or end of container
      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        gtlContent.push(currentElement.outerHTML)
        currentElement = currentElement.nextElementSibling
      }
      return gtlContent.length > 0
        ? {
            title,
            content: gtlContent.join(''),
          }
        : null
    } catch (error) {
      console.warn('Error extracting GTL Notes:', error)
      return null
    }
  }, [])

  const removeGtlNotesFromContent = useCallback((htmlContent) => {
    if (!htmlContent) return htmlContent
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    // Find the GTL Notes heading (same logic as extraction)
    const allGtlHeadings = doc.querySelectorAll('h3.linktogtl')
    let gtlHeading = null
    for (const heading of allGtlHeadings) {
      const anchorTag = heading.querySelector('a')
      if (anchorTag) {
        const text = anchorTag.textContent.trim()
        const id = anchorTag.id
        // Look for the actual GTL Notes heading
        if (text.startsWith('GTL Notes') && id === 'bookmark') {
          gtlHeading = heading
          break
        } else if (
          text.includes('GTL Notes') &&
          !text.includes('[') &&
          !text.includes(']')
        ) {
          gtlHeading = heading
          break
        }
      }
    }

    if (gtlHeading) {
      const elementsToRemove = [gtlHeading]
      // Remove all elements following the GTL Notes heading until next h3 or end
      let currentElement = gtlHeading.nextElementSibling
      while (currentElement) {
        if (currentElement.tagName === 'H3') {
          break
        }
        elementsToRemove.push(currentElement)
        currentElement = currentElement.nextElementSibling
      }
      // Remove all collected elements
      elementsToRemove.forEach((el) => el.remove())
    }
    return doc.body.innerHTML
  }, [])

  const memoizedGtlNotes = useMemo(() => {
    return treatyContent?.content
      ? extractGtlNotes(treatyContent.content)
      : null
  }, [treatyContent?.content, extractGtlNotes])

  // * Here we are creating dynamic link tag for this particular component
  // Add CSS styles specific to tax treaties
  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT || 'dev'
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://gtlcdn-eufeh8ffbvbvacgf.z03.azurefd.net/guide/stylesheets/${environment}/dtaa.css`
    link.id = 'tax-treaty-css'

    if (!document.getElementById('tax-treaty-css')) {
      document.head.appendChild(link)
    }

    return () => {
      document.getElementById('tax-treaty-css')?.remove() // Cleanup on unmount
    }
  }, [])
  // SEO Data Generation
  const generateSEOData = useCallback(() => {
    if (!treatyContent) return null

    // Extract country information
    const country2Name = treatyContent?.country2Name || 'Unknown Country'
    const translationStatus = treatyContent?.officialTranslation
      ? 'Official'
      : 'Unofficial'

    // Generate title based on treaty data
    const seoTitle = `${treatyContent?.country1Name}-${country2Name} Tax Treaty | DTAA | ${translationStatus} Translation | GCC Tax Laws`

    // Generate description from content
    const seoDescription =
      treatyContent?.summary ||
      (treatyContent?.textOnly
        ? treatyContent?.textOnly.substring(0, 155) + '...'
        : `Double Taxation Avoidance Agreement between ${treatyContent?.country1Name} and ${country2Name}. ${translationStatus} translation with complete treaty text and analysis.`)

    // Generate canonical URL
    const canonicalUrl = `https://gcctaxlaws.com/tax-treaties/${treatyContent.slug}`

    // Generate keywords
    const keywords = [
      `${treatyContent?.country1Name}-${country2Name} tax treaty`,
      'DTAA',
      'double taxation agreement',
      `${country2Name.toLowerCase()} treaty`,
      `${treatyContent?.country1Name} ${country2Name} tax treaty`,
      translationStatus.toLowerCase() + ' translation',
      'international tax',
    ]
      .filter(Boolean)
      .join(', ')

    // Generate schema markup
    const schemaMarkup = generateTreatySchema(treatyContent)

    return {
      title: seoTitle,
      description: seoDescription,
      canonical: canonicalUrl,
      keywords,
      schemaMarkup,
      publishedDate: treatyContent?.createdAt,
      modifiedDate: treatyContent?.updatedAt,
      country2Name,
      translationStatus,
      issueDate: treatyContent?.issueDate,
    }
  }, [treatyContent])
  useEffect(() => {
    // Fetch tax treaty content
    const getTreatyContent = async () => {
      // Skip if no slug or already fetching
      if (treaty?.content) {
        setTreatyContent(treaty)
        setLoading(false)
        return
      }

      if (!treaty?.slug || fetchedRef.current) {
        return
      }

      // Check module-level cache first
      if (contentCache[treaty.slug]) {
        // console.log('Using cached content for', treaty.slug)
        setTreatyContent(contentCache[treaty.slug])
        setLoading(false)
        return
      }

      // console.log('Fetching content for', treaty.slug)
      setLoading(true)
      fetchedRef.current = true
      try {
        const data = await apiService.getTaxTreatyBySlug(treaty.slug)
        // Store in module-level cache
        contentCache[treaty.slug] = data
        setTreatyContent(data)
        setError(null)
      } catch (err) {
        console.error('Error fetching tax treaty content:', err.message)
        setError(err.message)
        fetchedRef.current = false // Allow retry on error
      } finally {
        setLoading(false)
      }
    }
    getTreatyContent()
    // Cleanup function
    return () => {
      fetchedRef.current = false
    }
  }, [treaty])

  // Generate SEO data
  const seoData = generateSEOData()
  // Parse HTML content when article content changes, so we can check whether it contains any tables or not, if yes then we can show sort button
  useEffect(() => {
    if (treatyContent?.content) {
      // Parse the HTML content
      const parser = new DOMParser()
      const doc = parser.parseFromString(treatyContent.content, 'text/html')
      setParsedContent(doc)
      setSortedContent(null) // Reset sorted content when article changes

      // Extract GTL Notes
      if (memoizedGtlNotes) {
        setGtlNotesContent(memoizedGtlNotes.content)
        setGtlNotesTitle(memoizedGtlNotes.title)
        // Remove GTL Notes from main content
        const contentWithoutGtl = removeGtlNotesFromContent(
          treatyContent.content
        )
        setContentHtml(contentWithoutGtl)
      } else {
        setGtlNotesContent(null)
        setContentHtml(treatyContent.content)
      }
    }
  }, [treatyContent?.content, removeGtlNotesFromContent, memoizedGtlNotes])

  // below function is used to sort all the tables, we just need to sort the table where definitions are there in the article content
  const sortDefinitions = useCallback(() => {
    if (!parsedContent) return

    // Create a deep clone of the parsed content (without GTL Notes)
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(treatyContent?.content || '')
      : treatyContent?.content || ''
    const parser = new DOMParser()
    const doc = parser.parseFromString(baseContent, 'text/html')
    const docClone = doc.cloneNode(true)

    // Find all tables in the document
    // const tables = docClone.querySelectorAll('table')

    // Find only tables with class="definition"
    const tables = docClone.querySelectorAll('table.definition')

    tables.forEach((table) => {
      // Get all rows except header row (if any)
      const rows = Array.from(table.querySelectorAll('tr')).slice(1)

      if (rows.length > 0) {
        // Sort rows by the text in the first column
        rows.sort((a, b) => {
          const aText = a.querySelector('td:first-child')?.textContent || ''
          const bText = b.querySelector('td:first-child')?.textContent || ''
          return aText.localeCompare(bText)
        })

        // Remove existing rows
        rows.forEach((row) => row.parentNode.removeChild(row))

        // Append sorted rows
        const tbody = table.querySelector('tbody') || table
        rows.forEach((row) => tbody.appendChild(row))
      }
    })

    setSortedContent(docClone)
    setContentHtml(docClone.body.innerHTML)

    if (showSnackbar) {
      showSnackbar('Definitions sorted alphabetically', 'success')
    }
  }, [
    parsedContent,
    showSnackbar,
    memoizedGtlNotes,
    removeGtlNotesFromContent,
    treatyContent,
  ])

  // Reset to original content
  const resetToDefault = useCallback(() => {
    setSortedContent(null)
    // Reset to content without GTL Notes
    const baseContent = memoizedGtlNotes
      ? removeGtlNotesFromContent(treatyContent?.content || '')
      : treatyContent?.content || ''
    setContentHtml(baseContent)
    // Provide feedback when resetting
    if (showSnackbar) {
      showSnackbar('Restored original order', 'info')
    }
  }, [treatyContent, showSnackbar, memoizedGtlNotes, removeGtlNotesFromContent])

  const handleFeedbackFormToggle = useCallback(() => {
    setIsFeedbackFormOpen((prev) => !prev)
  }, [])
  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/tax-treaties/${
      treatyContent?.slug || treaty?.slug
    }`
    const title = treatyContent?.title || treaty?.title || 'Treaty'

    // Remove HTML tags from title for clean sharing
    const cleanTitle = title.replace(/<[^>]*>/g, '')

    const shareData = {
      title: cleanTitle,
      text: `Check out this Treaty: ${cleanTitle}`,
      url: url,
    }

    try {
      // Check if Web Share API is supported (mainly mobile)
      if (navigator.share) {
        await navigator.share(shareData)
        if (showSnackbar) {
          showSnackbar('Treaty shared successfully! 📤', 'success')
        }
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(url)
        if (showSnackbar) {
          showSnackbar('Treaty link copied to clipboard! 📋', 'success')
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback to clipboard if share fails
        try {
          await navigator.clipboard.writeText(url)
          if (showSnackbar) {
            showSnackbar('Treaty link copied to clipboard! 📋', 'success')
          }
        } catch (clipboardError) {
          if (showSnackbar) {
            showSnackbar('Failed to share treaty', 'error')
          }
        }
      }
    }
  }, [treatyContent, treaty, showSnackbar])
  const handlePrint = useCallback(() => {
    if (componentRef.current) {
      const wasAccordionExpanded = isAccordionExpanded

      // If GTL Notes exist and accordion is closed, expand it
      if (gtlNotesContent && !isAccordionExpanded) {
        setIsAccordionExpanded(true)
        // Wait for accordion to expand before printing
        setTimeout(() => {
          performPrint()
          // Restore original accordion state after printing
          setIsAccordionExpanded(wasAccordionExpanded)
        }, 300) // Give accordion time to expand
      } else {
        // No GTL Notes or accordion already expanded, print immediately
        performPrint()
      }
    }

    function performPrint() {
      const printWindow = window.open('', '_blank')
      printWindow.document.write(
        '<html><head><title>GCC Tax Laws - Tax Treaties</title>'
      )

      // Copy all styles from the current document to the print document
      const styleSheets = document.styleSheets
      for (let i = 0; i < styleSheets.length; i++) {
        if (styleSheets[i].href) {
          printWindow.document.write(
            `<link rel="stylesheet" type="text/css" href="${styleSheets[i].href}">`
          )
        } else {
          try {
            let css = ''
            for (let j = 0; j < styleSheets[i].cssRules.length; j++) {
              css += styleSheets[i].cssRules[j].cssText
            }
            printWindow.document.write(`<style>${css}</style>`)
          } catch (e) {
            console.warn('Error accessing stylesheet', e)
          }
        }
      }

      printWindow.document.write('</head><body>')
      printWindow.document.write(componentRef.current.innerHTML)
      printWindow.document.write('</body></html>')
      printWindow.document.close()
      printWindow.print()

      // Close the print window after printing
      printWindow.onafterprint = () => {
        printWindow.close()
      }
    }
    // }, [componentRef])
  }, [gtlNotesContent, isAccordionExpanded]) // remove componentRef from dependency array because the ref object itself doesn't change identity after the initial component mount.
  //       When you create a ref with useRef(), React creates the ref object once and gives you the same object on every render. Unlike state variables that trigger re-renders when updated, changing the value inside ref.current doesn't cause a re-render or change the ref object's identity.
  // Since the ref object itself never changes (only its .current property can change), including it in the dependency array of useCallback doesn't add any value. React will still create a new callback function on every render if componentRef is in the dependency array, even though the ref object is guaranteed to be stable.
  // By removing it from the dependency array, we're telling React: "This callback doesn't depend on any values from the render scope that might change between renders." This allows React to reuse the same callback function across renders, which is a small optimization.
  // In short, we removed it because:

  // It was unnecessary (the ref object is stable)
  // It prevented a small optimization (memoizing the callback)
  // It's cleaner to have an empty dependency array when the function truly doesn't depend on any changing values

  const hasDefinitionTables =
    parsedContent?.querySelectorAll('table.definition').length > 0
  // useEffect will work if you apply proper querySelector, for example you have written find all anchor tags who have target="_blank", but if you try to inspect the article in browser, you won't find the target attribute in anchor tag, maybe becuase of DOMPurify.sanitize()
  // Method 2 Trying html-react-parser -  avoiding potential memory leaks from improper handling of event listeners
  if (loading) {
    return (
      <Box sx={{ padding: 2, margin: 2 }}>
        <Skeleton variant='rectangular' width='100%' height={200} />
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='80%' />
      </Box>
    )
  }
  if (error) {
    return (
      <Enhanced404Handler
        error={error}
        article={treaty}
        showFullDetails={process.env.REACT_APP_ENVIRONMENT === 'dev'} // Show details only in dev
      />
    )
  }
  return (
    <>
      {/* SEO Component - Only add if not in modal and we have content */}
      {!isInModal && seoData && (
        <SEO
          title={seoData.title}
          description={seoData.description}
          canonical={seoData.canonical}
          keywords={seoData.keywords}
          schemaMarkup={seoData.schemaMarkup}
          publishedDate={seoData?.publishedDate}
          modifiedDate={seoData?.modifiedDate}
          country2Name={seoData.country2Name}
          translationStatus={seoData.translationStatus}
          issueDate={seoData?.issueDate}
          articleType='treaty'
        />
      )}
      {/* Beta Version Notice */}
      {treatyContent?.betaVersion && (
        <BetaVersionNotice
          show={treatyContent.betaVersion}
          isInModal={isInModal}
        />
      )}
      <Paper
        ref={componentRef}
        sx={{
          padding: 2,
          margin: 2,
          borderRadius: '8px',
          position: 'relative', // Make sure Paper has relative positioning
        }}
      >
        <Box display='flex' justifyContent='flex-end' alignItems='center'>
          <Box>
            {hasDefinitionTables && (
              <Tooltip
                title={
                  sortedContent
                    ? 'Restore the order of definitions back'
                    : 'Sort Definitions Alphabetically'
                }
                arrow
              >
                <IconButton
                  aria-label={
                    sortedContent
                      ? 'Restore the order of definitions back'
                      : 'Sort Definitions Alphabetically'
                  }
                  onClick={sortedContent ? resetToDefault : sortDefinitions}
                  size={isMobileScreen ? 'small' : 'medium'}
                >
                  {sortedContent ? (
                    <RestoreRoundedIcon sx={{ color: themeColors.primary }} />
                  ) : (
                    <SortByAlphaRoundedIcon
                      sx={{ color: themeColors.primary, fontSize: 28 }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                isFeedbackFormOpen
                  ? 'Close feedback form'
                  : 'Report Broken Links in this Treaty'
              }
              arrow
            >
              <IconButton
                aria-label={
                  isFeedbackFormOpen
                    ? 'Close feedback form'
                    : 'Report Broken Links in this Treaty'
                }
                // title={
                //   isFeedbackFormOpen
                //     ? 'Close feedback form'
                //     : 'Report Broken Links in this treaty'
                // }
                onClick={handleFeedbackFormToggle}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                {isFeedbackFormOpen ? (
                  <CloseRoundedIcon color='error' />
                ) : (
                  <ReportRoundedIcon color='error' />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title='Print this treaty' arrow>
              <IconButton
                aria-label='Print this treaty'
                // title='Print this treaty'
                onClick={handlePrint}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <PrintRoundedIcon
                  sx={{ color: themeColors.primary }}
                  // color='primary'
                />
              </IconButton>
            </Tooltip>
            <Tooltip title='Share this treaty' arrow>
              <IconButton
                aria-label='Share this treaty'
                onClick={handleShare}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <ShareRounded sx={{ color: themeColors.primary }} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Mail this treaty' arrow>
              <IconButton
                aria-label='Mail this treaty'
                // title='Mail this treaty'
                onClick={() => handleEmail(treatyContent || treaty)}
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <MailRoundedIcon
                  sx={{ color: themeColors.primary }}
                  // color='primary'
                />
              </IconButton>
            </Tooltip>
            <Tooltip title='Open this treaty in new tab' arrow>
              <IconButton
                aria-label='Open this treaty in new tab'
                // title='Open this treaty in new tab'
                onClick={() =>
                  handleOpenInNewTab(
                    treatyContent || treaty,
                    `/tax-treaties/${treaty?.slug}`,
                    showSnackbar
                  )
                }
                size={isMobileScreen ? 'small' : 'medium'}
              >
                <OpenInNewRoundedIcon
                  sx={{ color: themeColors.primary }}
                  // color='primary'
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isFeedbackFormOpen && (
          <FeedbackForm
            articleId={treatyContent?.slug || treaty.slug}
            onClose={handleFeedbackFormToggle}
            showSnackbar={showSnackbar}
          />
        )}

        {contentHtml ? (
          <Box mt={2}>
            {parseHtmlSanitizeAddTargetToLinks(
              contentHtml,
              searchQuery,
              '/tax-treaties/' + (treatyContent?.slug || treaty?.slug),
              {
                fullDocumentContent: treatyContent?.content || contentHtml,
                documentTitle:
                  treatyContent?.title || treaty?.title || 'Tax-Treaty',
                showSnackbar: showSnackbar,
              }
            )}

            {/* MLI content (if available) */}
            {treatyContent?.mliContent && (
              <>
                <Typography variant='h6' mt={4} mb={2}>
                  MLI Provisions
                </Typography>
                <Typography>
                  {parseHtmlSanitizeAddTargetToLinks(
                    treatyContent.mliContent,
                    searchQuery,
                    '/tax-treaties/' + (treatyContent?.slug || treaty?.slug),
                    {
                      fullDocumentContent:
                        treatyContent?.mliContent || contentHtml,
                      documentTitle:
                        treatyContent?.title || treaty?.title || 'Tax-Treaty',
                      showSnackbar: showSnackbar,
                    }
                  )}
                </Typography>
              </>
            )}
            {/* GTL Notes Accordion */}
            {gtlNotesContent && (
              <Box mt={3}>
                <Accordion
                  expanded={isAccordionExpanded}
                  onChange={(event, expanded) =>
                    setIsAccordionExpanded(expanded)
                  }
                  aria-label='GTL Notes section'
                  className='gtl-notes-accordion'
                  sx={{
                    '&:before': {
                      display: 'none',
                    },
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px !important',
                    '&:not(:last-child)': {
                      borderBottom: 0,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className='gtl-notes-accordion-summary'
                    sx={{
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.03)',
                      borderRadius: '8px',
                      '&.Mui-expanded': {
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  >
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 600,
                        color: '#43af76',
                      }}
                    >
                      {gtlNotesTitle}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ padding: 2 }}
                    className='gtl-notes-accordion-details'
                  >
                    <Box className='gtl-notes-content'>
                      {parseHtmlSanitizeAddTargetToLinks(
                        gtlNotesContent,
                        searchQuery,
                        '/tax-treaties/' +
                          (treatyContent?.slug || treaty?.slug),
                        {
                          fullDocumentContent:
                            treatyContent?.content || gtlNotesContent,
                          documentTitle:
                            treatyContent?.title ||
                            treaty?.title ||
                            'Tax-Treaty',
                          showSnackbar: showSnackbar,
                        }
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>
            We're currently building the most comprehensive tax law repository
            for the GCC region. If you happen to have this particular document,
            we’d be truly grateful if you could share it with us.
            <br />
            Your contribution will not only enhance our database but also help
            the entire community access accurate legal information.
            <br />
            <br />
            📧 Mail us at:{' '}
            <strong>
              <Link
                href='mailto:support@gcctaxlaws.com'
                underline='none'
                sx={{
                  color: '#5948ca',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                support@gcctaxlaws.com
              </Link>
            </strong>
          </Typography>
        )}
      </Paper>

      {/* Unofficial Translation Notice - outside the Paper component but within the fragment */}
      {treatyContent?.officialTranslation === false && (
        <UnofficialTranslationNotice
          show={!treatyContent?.officialTranslation}
          isInModal={isInModal}
        />
      )}
      {/* Back to Top button - only shown in standalone mode (non-modal), not in modal */}
      {!isInModal && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <Zoom in={showBackToTop}>
            {isMediumScreen ? (
              <Fab
                size='small'
                aria-label='Scroll back to top'
                onClick={scrollToTop}
                sx={{
                  backgroundColor: themeColors.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: themeColors.hover,
                  },
                }}
              >
                <KeyboardArrowUpIcon fontSize='small' />
              </Fab>
            ) : (
              <Fab
                variant='extended'
                size='medium'
                aria-label='Scroll back to top'
                onClick={scrollToTop}
                sx={{
                  backgroundColor: themeColors.primary,
                  color: 'white',
                  '&:hover': { backgroundColor: themeColors.hover },
                  paddingLeft: 2,
                  paddingRight: 2,
                  borderRadius: buttonBorderRadius,
                }}
              >
                <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                <Typography
                  variant='button'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Back to Top
                </Typography>
              </Fab>
            )}
          </Zoom>
        </Box>
      )}
    </>
  )
}

export default TaxTreatyArticle
